import React, { Suspense } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  formatCurrency,
  formatDate,
  formatText,
  formatVehicleNumber,
} from "../../../utils/utils";
import { getValidToken } from "../../../utils/authUtils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useReportFilter } from "../context/ReportFilterContext";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";
import { useResponsiveBreakpoints } from "../../../utils/responsiveUtils";

const OperatorSummary = React.lazy(
  () => import("../../trip/dailyBusTrip/components/OperatorSummary")
);

interface TripIncome {
  total: number;
  BUS: {
    total: number;
    contract: number;
    commission: number;
    records: {
      contract: Array<{
        id: string;
        bookingType: "BUS";
        bookingMode: "CONTRACT" | "COMMISSION";
        amount: number;
        journeyType: "START" | "RETURN";
        date: string;
        operator: string;
      }>;
      commission: Array<{
        id: string;
        operator: string;
        operatorId: string | null;
        operatorType: string | null;
        fromId: string | null;
        toId: string | null;
        srNumber: string;
        from: string;
        to: string;
        seatNames: string;
        numberOfSeats: number;
        basePrice: number;
        soldPrice: number;
        total: number;
        received: number;
        pending: number;
        operatorCommissionPercent: number;
        operatorCommission: number;
        gstPercentage: number;
        gstAmount: number;
        netAmount: number;
        agentBranchCommission: number;
        agentBranchCommissionPercent: number;
        agentBranch: string;
      }>;
    };
  };
  LUGGAGE: {
    total: number;
    contract: number;
    commission: {
      commissionAmount: number;
      byCommissionType: {
        FIXED: {
          amount: number;
          commissionAmount: number;
        };
        PERCENTAGE: {
          amount: number;
          commissionAmount: number;
        };
      };
    };
    pickup: {
      commissionAmount: number;
      byCommissionType: {
        FIXED: {
          amount: number;
          commissionAmount: number;
        };
        PERCENTAGE: {
          amount: number;
          commissionAmount: number;
        };
      };
    };
    records: {
      contract: Array<{
        id: string;
        bookingType: "LUGGAGE";
        bookingMode: "CONTRACT";
        amount: number;
        journeyType: "START" | "RETURN";
        date: string;
        bookingAmount: number;
        commissionAmount: number;
        operator: string;
        lSNo?: string;
        quantity?: string;
        pickupLocation?: string;
        dropLocation?: string;
        paid?: number;
        toPay?: number;
        operatorContactNumber?: string;
      }>;
      commission: Array<{
        id: string;
        bookingType: "LUGGAGE";
        bookingMode: "COMMISSION";
        amount: number;
        journeyType: "START" | "RETURN";
        date: string;
        bookingAmount: number;
        commissionAmount: number;
        operator: string;
        lSNo?: string;
        quantity?: string;
        pickupLocation?: string;
        dropLocation?: string;
        paid?: number;
        toPay?: number;
        operatorContactNumber?: string;
        pricingValue?: string;
        pricingType?: string;
      }>;
      pickup: Array<{
        id: string;
        bookingType: "LUGGAGE";
        bookingMode: "PICKUP";
        amount: number;
        journeyType: "START" | "RETURN";
        date: string;
        bookingAmount: number;
        commissionAmount: number;
        operator: string;
        lSNo?: string;
        quantity?: string;
        pickupLocation?: string;
        dropLocation?: string;
        paid?: number;
        toPay?: number;
        operatorContactNumber?: string;
        pricingValue?: string;
        pricingType?: string;
      }>;
    };
  };
  records: Array<{
    id: string;
    bookingType: "BUS" | "LUGGAGE";
    bookingMode: "CONTRACT" | "COMMISSION" | "PICKUP" | "INCLUDED";
    amount: number;
    journeyType: "START" | "RETURN";
    date: string;
  }>;
}

interface ExpenseCategory {
  total: number;
  records: Array<{
    id: string;
    amount: number;
    date: string;
    [key: string]: any; // Additional fields vary by category
  }>;
}

interface TripExpenses {
  fuel: ExpenseCategory & {
    records: Array<{
      quantity: string;
      location: string;
      provider: string;
      odometerReading: number | null;
      notes: string | null;
    }>;
  };
  maintenance: ExpenseCategory & {
    records: Array<{
      description: string;
      location: string;
      mechanicName: string;
      mechanicContactNumber: string;
      notes: string;
    }>;
  };
  driver_cleaner: ExpenseCategory & {
    records: Array<{
      type: "DRIVER";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
  parking: ExpenseCategory & {
    records: Array<{
      type: "PARKING";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
  challan: ExpenseCategory & {
    records: Array<{
      type: "CHALLAN";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
  owner: ExpenseCategory & {
    records: Array<{
      type: "OWNER";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
  pickup_man: ExpenseCategory & {
    records: Array<{
      type: "PICKUP_MAN";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
  insurance: ExpenseCategory & {
    records: Array<{
      type: "INSURANCE";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
  other: ExpenseCategory & {
    records: Array<{
      type: "OTHER";
      location: string;
      paidTo: string;
      contactNumber: string;
    }>;
  };
}

interface TripEntry {
  tripId: string;
  vehicleNumber: string;
  fromLocation: string;
  toLocation: string;
  journeyStartDate: string;
  journeyReturnDate: string | null;
  tripType: "Single" | "Round";
  income: TripIncome;
  expenses: TripExpenses;
}

interface PNLReportData {
  finalReportSummary: {
    totalTrips: number;
    totalIncome: number;
    totalExpense: number;
    totalCommission: number;
    totalProfit: number;
  };
  incomeSummary: {
    totalIncome: number;
    totalBusIncome: number;
    totalLuggageIncome: number;
    byType: {
      BUS: {
        totalIncome: number;
        contractIncome: number;
        commissionIncome: number;
      };
      LUGGAGE: {
        totalIncome: number;
        contractIncome: number;
        commissionIncome: number;
      };
    };
  };
  expenseSummary: {
    totalExpense: number;
    byCategory: {
      fuel: ExpenseCategory;
      maintenance: ExpenseCategory;
      driver: ExpenseCategory;
      cleaner: ExpenseCategory;
      toll: ExpenseCategory;
      parking: ExpenseCategory;
      challan: ExpenseCategory;
      rto: ExpenseCategory;
    };
  };
  detailedTrips: TripEntry[];
}

// Responsive table styles factory
const createTableStyles = (isMobile: boolean, isTablet: boolean) => ({
  tableHeaderStyle: {
    padding: isMobile ? "4px 6px" : isTablet ? "5px 8px" : "6px 12px",
    textAlign: "left" as const,
    backgroundColor: "#f5f5f5",
    borderBottom: "1px solid #ddd",
    fontSize: isMobile ? "10px" : isTablet ? "11px" : "12px",
    wordBreak: "break-word" as const,
  },
  tableCellStyle: {
    padding: isMobile ? "4px 6px" : isTablet ? "5px 8px" : "6px 12px",
    borderBottom: "1px solid #ddd",
    fontSize: isMobile ? "10px" : isTablet ? "11px" : "12px",
    wordBreak: "break-word" as const,
  },
});

const PNLReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const { timeFilter, customStartDate, customEndDate, selectedVehicles } =
    filterState;

  const navigate = useNavigate();

  // Use responsive breakpoints for layout optimization
  const { isMobile, isTablet, isSmallDesktop, getSpacing, screenCategory } =
    useResponsiveBreakpoints();

  // Create responsive table styles
  const { tableHeaderStyle, tableCellStyle } = createTableStyles(
    isMobile,
    isTablet
  );

  // Fetch PNL report data
  const {
    data: reportData,
    isLoading,
    isFetching,
    isError,
  } = useQuery<{ data: PNLReportData }>({
    queryKey: [
      "pnlReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/pnl/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/pnl/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      if (selectedVehicles.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  const calculateTotalExpenses = (expenses: TripExpenses): number => {
    if (!expenses) return 0;

    return (
      (expenses.fuel?.total ?? 0) +
      (expenses.maintenance?.total ?? 0) +
      (expenses.parking?.total ?? 0) +
      (expenses.driver_cleaner?.total ?? 0) +
      (expenses.challan?.total ?? 0) +
      (expenses.owner?.total ?? 0) +
      (expenses.pickup_man?.total ?? 0) +
      (expenses.insurance?.total ?? 0) +
      (expenses.other?.total ?? 0)
    );
  };

  // Show loading for initial load

  return (
    <Box sx={{ position: "relative" }}>
      {/* Loading overlay for refetching */}
      {isLoading && <Loading fullScreen={false} message="Loading..." />}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing..." />
      )}

      {/* Summary Cards */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: isMobile ? 1 : 2,
          flexWrap: "wrap",
        }}
      >
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            py: 1.5,
            gap: isMobile ? 0.5 : 2,
            minWidth: isMobile ? "45%" : "auto",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography fontSize={isMobile ? 11 : 12}>Total Trips</Typography>
          <Typography variant="h4" fontSize={isMobile ? 13 : 14}>
            {reportData?.data?.finalReportSummary?.totalTrips ?? 0}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            py: 1.5,
            gap: isMobile ? 0.5 : 2,
            minWidth: isMobile ? "45%" : "auto",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography fontSize={isMobile ? 11 : 12}>Income</Typography>
          <Typography variant="h4" fontSize={isMobile ? 13 : 14}>
            {formatCurrency(
              reportData?.data?.finalReportSummary?.totalIncome ?? 0
            )}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            py: 1.5,
            gap: isMobile ? 0.5 : 2,
            minWidth: isMobile ? "45%" : "auto",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography fontSize={isMobile ? 11 : 12}>Commission</Typography>
          <Typography variant="h4" fontSize={isMobile ? 13 : 14}>
            {formatCurrency(
              reportData?.data?.finalReportSummary?.totalCommission ?? 0
            )}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            py: 1.5,
            gap: isMobile ? 0.5 : 2,
            minWidth: isMobile ? "45%" : "auto",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography fontSize={isMobile ? 11 : 12}>Expense</Typography>
          <Typography variant="h4" fontSize={isMobile ? 13 : 14}>
            {formatCurrency(
              reportData?.data?.finalReportSummary?.totalExpense ?? 0
            )}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            py: 1.5,
            gap: isMobile ? 0.5 : 2,
            minWidth: isMobile ? "45%" : "auto",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography fontSize={isMobile ? 11 : 12}>Net Income</Typography>
          <Typography
            variant="h4"
            fontSize={isMobile ? 13 : 14}
            sx={{
              color:
                (reportData?.data?.finalReportSummary?.totalProfit ?? 0) >= 0
                  ? "#4caf50"
                  : "#f44336",
            }}
          >
            {formatCurrency(
              reportData?.data?.finalReportSummary?.totalProfit ?? 0
            )}
          </Typography>
        </Paper>
      </Box>

      <Grid container spacing={isMobile ? 1 : isTablet ? 1.5 : 2}>
        {/* Trip Details with Accordion */}
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 1 : isTablet ? 1.5 : 2 }}>
            <Typography
              fontSize={isMobile ? 12 : isTablet ? 13 : 12}
              fontWeight={600}
              mb={isMobile ? 1 : 2}
            >
              Trip Details
            </Typography>
            <Box>
              {reportData?.data?.detailedTrips?.map((trip) => {
                const totalExpenses = calculateTotalExpenses(trip.expenses);
                const totalCommission =
                  trip.income?.BUS?.commission +
                  trip.income?.LUGGAGE?.commission.commissionAmount +
                  trip.income?.LUGGAGE?.pickup?.commissionAmount;
                const profit =
                  (trip.income?.total ?? 0) - totalExpenses - totalCommission;

                return (
                  <Accordion
                    key={trip.tripId}
                    sx={{
                      mb: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px !important",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      "&:before": {
                        display: "none",
                      },
                      "&.Mui-expanded": {
                        margin: "8px 0",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#666" }} />}
                      sx={{
                        minHeight: "48px !important",
                        py: 1,
                        px: 2,
                        backgroundColor: "#fafafa",
                        borderRadius: "8px",
                        "&.Mui-expanded": {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                          borderBottom: "1px solid #e0e0e0",
                        },
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 1.5,
                          margin: "8px 0 !important",
                          "&.Mui-expanded": {
                            margin: "8px 0 !important",
                          },
                        },
                      }}
                    >
                      {isMobile ? (
                        // Mobile layout - stacked vertically (similar to RentalJourneyPNL mobile view)
                        <Box sx={{ width: "100%", textAlign: "left" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "11px",
                                cursor: "pointer",
                                textDecoration: "underline",
                                color: "primary.main",
                                fontWeight: 600,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/trip/daily-bus/${trip.tripId}/basic-details`
                                );
                              }}
                            >
                              {formatVehicleNumber(trip.vehicleNumber)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: profit >= 0 ? "#4caf50" : "#f44336",
                                backgroundColor:
                                  profit >= 0 ? "#e8f5e8" : "#ffebee",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "8px",
                                lineHeight: 1,
                              }}
                            >
                              {formatCurrency(profit)}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#666",
                              mb: 0.75,
                              lineHeight: 1.3,
                            }}
                          >
                            {trip.fromLocation ?? ""} → {trip.toLocation ?? ""}{" "}
                            - {trip.tripType}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#666",
                              mb: 0.75,
                              lineHeight: 1.3,
                            }}
                          >
                            {trip.journeyStartDate
                              ? formatDate(trip.journeyStartDate)
                              : ""}
                            {trip.journeyReturnDate &&
                              ` - ${formatDate(trip.journeyReturnDate)}`}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "11px", fontWeight: 600 }}
                            >
                              Income: {formatCurrency(trip.income?.total ?? 0)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#808080",
                              }}
                            >
                              Expense: {formatCurrency(totalExpenses)}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        // Desktop layout - horizontal (matching RentalJourneyPNL style)
                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          {/* Date Column */}
                          <Box
                            sx={{
                              width: "12%",
                              display: "flex",
                              flexDirection: "column",
                              cursor: "pointer",
                              pr: 1,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/trip/daily-bus/${trip.tripId}/basic-details`
                              );
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "primary.main",
                                textDecoration: "underline",
                                lineHeight: 1.3,
                              }}
                            >
                              {trip.journeyStartDate
                                ? formatDate(trip.journeyStartDate)
                                : "-"}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "9px",
                                color: "#666",
                                lineHeight: 1.2,
                                mt: 0.25,
                              }}
                            >
                              {trip.journeyReturnDate
                                ? formatDate(trip.journeyReturnDate)
                                : "Single"}
                            </Typography>
                          </Box>

                          {/* Vehicle Number */}

                          <Typography
                            sx={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: "#1976d2",
                              py: 0.25,
                              lineHeight: 1,
                            }}
                          >
                            {formatVehicleNumber(trip.vehicleNumber)}
                          </Typography>

                          {/* Route */}
                          <Box
                            sx={{
                              width: "20%",
                              display: "flex",
                              alignItems: "center",
                              pr: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "10px",
                                color: "#666",
                                lineHeight: 1.3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {trip.fromLocation ?? ""} →{" "}
                              {trip.toLocation ?? ""} - {trip.tripType}
                            </Typography>
                          </Box>

                          {/* Income */}
                          <Box
                            sx={{
                              width: "11%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              pr: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                color: "#666",
                                lineHeight: 1,
                                mb: 0.25,
                              }}
                            >
                              Income
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                fontWeight: 600,
                                lineHeight: 1.2,
                                color: "#4caf50",
                              }}
                            >
                              {formatCurrency(trip.income?.total ?? 0)}
                            </Typography>
                          </Box>

                          {/* Expense */}
                          <Box
                            sx={{
                              width: "11%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              pr: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                color: "#666",
                                lineHeight: 1,
                                mb: 0.25,
                              }}
                            >
                              Expense
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#f44336",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(totalExpenses)}
                            </Typography>
                          </Box>

                          {/* Commission */}
                          <Box
                            sx={{
                              width: "11%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              pr: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                color: "#666",
                                lineHeight: 1,
                                mb: 0.25,
                              }}
                            >
                              Commission
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#ff9800",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(totalCommission)}
                            </Typography>
                          </Box>

                          {/* Profit/Loss */}
                          <Box
                            sx={{
                              width: "11%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                color: "#666",
                                lineHeight: 1,
                                mb: 0.25,
                              }}
                            >
                              P&L
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                fontWeight: "bold",
                                color: profit >= 0 ? "#4caf50" : "#f44336",
                                backgroundColor:
                                  profit >= 0 ? "#e8f5e8" : "#ffebee",
                                px: 0.5,
                                py: 0.25,
                                borderRadius: "5px",
                                lineHeight: 1,
                              }}
                            >
                              {formatCurrency(profit)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Income Details */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Income Details
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              fontSize={11}
                              sx={{ mb: 1 }}
                            >
                              Bus Income{" "}
                              {trip.income?.BUS?.total > 0 &&
                                `(${formatCurrency(trip.income?.BUS?.total)})`}
                            </Typography>
                            {trip.income?.BUS?.records.contract?.length === 0 &&
                              trip.income?.BUS?.records.commission?.length ===
                                0 && (
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  fontSize={12}
                                  sx={{ mb: 1 }}
                                >
                                  No bus income records found
                                </Typography>
                              )}
                            {trip.income?.BUS?.records?.contract?.length >
                              0 && (
                              <Box
                                sx={{
                                  overflowX: isMobile ? "auto" : "visible",
                                  mb: 1,
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    minWidth: isMobile ? "400px" : "auto",
                                    borderCollapse: "collapse",
                                    marginBottom: "1rem",
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      <th style={tableHeaderStyle}>Type</th>
                                      <th style={tableHeaderStyle}>Journey</th>
                                      <th style={tableHeaderStyle}>Amount</th>
                                      <th style={tableHeaderStyle}>Operator</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {trip.income.BUS.records.contract.map(
                                      (record) => (
                                        <tr key={record.id}>
                                          <td style={tableCellStyle}>
                                            {record.bookingMode}
                                          </td>
                                          <td style={tableCellStyle}>
                                            {record.journeyType}
                                          </td>
                                          <td style={tableCellStyle}>
                                            {formatCurrency(record.amount)}
                                          </td>
                                          <td style={tableCellStyle}>
                                            {record.operator}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </Box>
                            )}
                            {trip.income?.BUS?.records?.commission?.length >
                              0 && (
                              <Suspense
                                fallback={
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{ mt: 3, textAlign: "center" }}
                                  >
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      Loading summary...
                                    </Typography>
                                  </Grid>
                                }
                              >
                                <OperatorSummary
                                  bookingData={
                                    trip.income?.BUS?.records?.commission
                                  }
                                />
                              </Suspense>
                            )}

                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              fontSize={12}
                              sx={{ mb: 1, mt: 1 }}
                            >
                              Luggage Income{" "}
                              {(trip.income?.LUGGAGE?.records?.contract
                                .length ||
                                trip.income?.LUGGAGE?.records?.commission
                                  .length ||
                                trip.income?.LUGGAGE?.records?.pickup.length) &&
                                `(${formatCurrency(
                                  trip.income?.LUGGAGE?.total
                                )})`}
                            </Typography>
                            {trip.income?.LUGGAGE?.records?.contract.length ||
                            trip.income?.LUGGAGE?.records?.commission.length ||
                            trip.income?.LUGGAGE?.records?.pickup.length ? (
                              <>
                                {/* Contract Records */}
                                {trip.income.LUGGAGE.records.contract.length >
                                  0 && (
                                  <>
                                    <Typography
                                      variant="subtitle2"
                                      color="text.secondary"
                                      fontSize={12}
                                      sx={{ mb: 1 }}
                                    >
                                      Contract Records
                                    </Typography>
                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        marginBottom: "1rem",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th style={tableHeaderStyle}>
                                            Operator
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Journey
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {trip.income.LUGGAGE.records.contract.map(
                                          (record) => (
                                            <tr key={record.id}>
                                              <td style={tableCellStyle}>
                                                {record.operator}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.journeyType}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {formatCurrency(record.amount)}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </>
                                )}

                                {/* Commission Records */}
                                {trip.income.LUGGAGE.records.commission.length >
                                  0 && (
                                  <>
                                    <Typography
                                      variant="subtitle2"
                                      color="text.secondary"
                                      fontSize={12}
                                      sx={{ mb: 1 }}
                                    >
                                      Commission Records
                                    </Typography>
                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        marginBottom: "1rem",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th style={tableHeaderStyle}>
                                            LS No.
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Operator
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Journey
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Quantity
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Pickup
                                          </th>
                                          <th style={tableHeaderStyle}>Drop</th>
                                          <th style={tableHeaderStyle}>
                                            Paid/To Pay
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Amount
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Pricing
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Commission
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {trip.income.LUGGAGE.records.commission.map(
                                          (record) => (
                                            <tr key={record.id}>
                                              <td style={tableCellStyle}>
                                                {record.lSNo || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.operator}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.journeyType}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.quantity || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.pickupLocation || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.dropLocation || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {`Paid: ${formatCurrency(
                                                  record.paid ?? 0
                                                )} / To Pay: ${formatCurrency(
                                                  record.toPay ?? 0
                                                )}`}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {formatCurrency(record.amount)}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.pricingType ===
                                                "Percentage"
                                                  ? `${record.pricingValue}%`
                                                  : record.pricingType ===
                                                    "Fixed"
                                                  ? `${formatCurrency(
                                                      Number(
                                                        record.pricingValue
                                                      )
                                                    )} / item`
                                                  : record.pricingValue}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.commissionAmount
                                                  ? formatCurrency(
                                                      record.commissionAmount
                                                    )
                                                  : "-"}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </>
                                )}

                                {/* Pickup Records */}
                                {trip.income.LUGGAGE.records.pickup.length >
                                  0 && (
                                  <>
                                    <Typography
                                      variant="subtitle2"
                                      color="text.secondary"
                                      fontSize={12}
                                      sx={{ mb: 1 }}
                                    >
                                      Pickup Records
                                    </Typography>
                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th style={tableHeaderStyle}>
                                            LS No.
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Operator
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Journey
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Quantity
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Pickup
                                          </th>
                                          <th style={tableHeaderStyle}>Drop</th>
                                          <th style={tableHeaderStyle}>
                                            Paid/To Pay
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Amount
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Pricing
                                          </th>
                                          <th style={tableHeaderStyle}>
                                            Commission
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {trip.income.LUGGAGE.records.pickup.map(
                                          (record) => (
                                            <tr key={record.id}>
                                              <td style={tableCellStyle}>
                                                {record.lSNo || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.operator}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.journeyType}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.quantity || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.pickupLocation || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.dropLocation || "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {(record.paid ?? 0) > 0 &&
                                                  `Paid: ${formatCurrency(
                                                    record.paid ?? 0
                                                  )}`}
                                                {(record.paid ?? 0) > 0 &&
                                                  (record.toPay ?? 0) > 0 &&
                                                  " / "}
                                                {(record.toPay ?? 0) > 0 &&
                                                  `To Pay: ${formatCurrency(
                                                    record.toPay ?? 0
                                                  )}`}
                                                {!(record.paid ?? 0) &&
                                                  !(record.toPay ?? 0) &&
                                                  "-"}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {formatCurrency(record.amount)}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.pricingType ===
                                                "Percentage"
                                                  ? `${record.pricingValue}%`
                                                  : record.pricingType ===
                                                    "Fixed"
                                                  ? `${formatCurrency(
                                                      Number(
                                                        record.pricingValue
                                                      )
                                                    )} / item`
                                                  : record.pricingValue}
                                              </td>
                                              <td style={tableCellStyle}>
                                                {record.commissionAmount
                                                  ? formatCurrency(
                                                      record.commissionAmount
                                                    )
                                                  : "-"}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </>
                                )}
                              </>
                            ) : (
                              <Typography
                                color="text.secondary"
                                fontSize={12}
                                sx={{ my: 1 }}
                              >
                                No luggage income records found
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Expense Details */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Expense Details
                          </Typography>
                          {Object.entries(trip.expenses ?? {}).some(
                            ([_, category]) => category?.records?.length > 0
                          ) ? (
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th style={tableHeaderStyle}>Category</th>
                                  <th style={tableHeaderStyle}>Date</th>
                                  <th style={tableHeaderStyle}>Details</th>
                                  <th style={tableHeaderStyle}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Other Expenses */}
                                {[
                                  "fuel",
                                  "driver_cleaner",
                                  "maintenance",
                                  "parking",
                                  "challan",
                                  "owner",
                                  "pickup_man",
                                  "insurance",
                                  "other",
                                ].map((category) =>
                                  trip.expenses[
                                    category as keyof TripExpenses
                                  ]?.records?.map((record) => (
                                    <tr key={record.id}>
                                      <td style={tableCellStyle}>
                                        {category === "driver_cleaner"
                                          ? formatText(record?.category)
                                          : formatText(category)}
                                      </td>
                                      <td style={tableCellStyle}>
                                        {formatDate(record.date)}
                                      </td>
                                      {category === "other" ? (
                                        <td style={tableCellStyle}>
                                          Type : {record.type}
                                        </td>
                                      ) : (
                                        <td style={tableCellStyle}>
                                          {record.location
                                            ? record.location
                                            : record.paidTo
                                            ? `Paid to: ${record.paidTo}`
                                            : "-"}
                                          {record.location &&
                                            record.paidTo &&
                                            ` - Paid to: ${record.paidTo}`}
                                        </td>
                                      )}
                                      <td style={tableCellStyle}>
                                        {formatCurrency(record.amount)}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td
                                    colSpan={3}
                                    style={{
                                      ...tableHeaderStyle,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Total Expenses
                                  </td>
                                  <td
                                    style={{
                                      ...tableHeaderStyle,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {formatCurrency(totalExpenses)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          ) : (
                            <Typography
                              color="text.secondary"
                              fontSize={12}
                              sx={{ my: 1 }}
                            >
                              No expense records found
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              {reportData?.data?.detailedTrips.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No Trip P&L data available
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PNLReport;
